import React from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import type { FeedbackAnalyticsDto, RatingValue } from '../../../../services/feedback-analytics';
import InfoHint from './InfoHint';

interface RatingDistributionChartProps {
  data: FeedbackAnalyticsDto | null;
  loading?: boolean;
}

export const RatingDistributionChart: React.FC<RatingDistributionChartProps> =
  ({ data, loading = false }) => {
    if (!data || data.ratingDistribution.length === 0) {
      return (
        <div className="alert alert-info text-center my-4">
          Nenhum dado de distribuição disponível
        </div>
      );
    }

    const chartOptions: ApexCharts.ApexOptions = {
      chart: {
        type: 'bar',
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        sparkline: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          columnWidth: '45%',
          borderRadius: 4,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ['#304758'],
        },
      },
      xaxis: {
        categories: data.ratingDistribution.map((r: RatingValue) => `${r.score}`),
        title: {
          text: 'Nota',
        },
      },
      yaxis: {
        title: {
          text: 'Quantidade',
        },
        forceNiceScale: true,
      },
      colors: ['#0d6efd'],
      tooltip: {
        y: {
          formatter: (val: number) => `${val} avaliações`,
        },
      },
    };

    const chartSeries = [
      {
        name: 'Quantidade de Avaliações',
        data: data.ratingDistribution.map((r: RatingValue) => r.count),
      },
    ];

    return (
      <Card className="shadow-sm border-0 mb-5">
        <Card.Header className="bg-white border-bottom">
          <Card.Title className="mb-0 d-flex align-items-center">
            Distribuição de Notas e Segmentos NPS
            <InfoHint
              id="rating-nps-segments"
              text="Mostra a frequência das notas e a divisão NPS em Promotores (9-10), Neutros (7-8) e Detratores (0-6)."
            />
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 mb-4">
            <Col xs={12} md={4}>
              <div className="bg-light rounded p-3">
                <small className="text-muted d-block">Promotores (9-10)</small>
                <h5 className="mb-1 text-success">{data.npsSummary.promotersCount}</h5>
                <Badge bg="success">{data.npsSummary.promotersPercentage}%</Badge>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="bg-light rounded p-3">
                <small className="text-muted d-block">Neutros (7-8)</small>
                <h5 className="mb-1 text-warning">{data.npsSummary.passivesCount}</h5>
                <Badge bg="warning" text="dark">
                  {data.npsSummary.passivesPercentage}%
                </Badge>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="bg-light rounded p-3">
                <small className="text-muted d-block">Detratores (0-6)</small>
                <h5 className="mb-1 text-danger">{data.npsSummary.detractorsCount}</h5>
                <Badge bg="danger">{data.npsSummary.detractorsPercentage}%</Badge>
              </div>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="bar"
              height={400}
            />
          )}
        </Card.Body>
      </Card>
    );
  };

export default RatingDistributionChart;
